// src/pages/sessions/components/LiveSession/breakout/BreakoutRoomPanel.jsx
import React, { useState, useMemo, useCallback, memo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParticipants, useRoomContext } from '@livekit/components-react';
import {
  Squares2X2Icon,
  UserGroupIcon,
  PlusIcon,
  VideoCameraIcon,
  UserIcon,
  UserPlusIcon,
  PlusCircleIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
  QueueListIcon,
  SpeakerXMarkIcon
} from '@heroicons/react/24/outline';

const HINT_KEY = 'sc_breakout_hint_v1';

const BreakoutRoomHint = memo(() => {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(() => localStorage.getItem(HINT_KEY) !== 'dismissed');

  const dismiss = useCallback(() => {
    localStorage.setItem(HINT_KEY, 'dismissed');
    setVisible(false);
  }, []);

  if (!visible) return null;

  return (
    <div className="mx-4 mb-3 bg-gray-700 border border-cyan-700/50 rounded-lg p-3 relative">
      {/* small arrow pointing right toward buttons */}
      <div className="absolute right-[-7px] top-1/2 -translate-y-1/2 w-0 h-0
        border-t-[6px] border-t-transparent
        border-b-[6px] border-b-transparent
        border-l-[7px] border-l-gray-700" />

      <p className="text-xs font-semibold text-cyan-400 mb-2">{t('breakout.hintTitle')}</p>

      <div className="space-y-1.5">
        <div className="flex items-start gap-2">
          <span className="flex-shrink-0 mt-0.5 flex items-center justify-center w-5 h-5 rounded bg-cyan-900/60 text-cyan-400">
            <PlusIcon className="w-3 h-3" />
          </span>
          <p className="text-xs text-gray-300 leading-tight">{t('breakout.hintJoin')}</p>
        </div>
        <div className="flex items-start gap-2">
          <span className="flex-shrink-0 mt-0.5 flex items-center justify-center w-5 h-5 rounded bg-purple-900/60 text-purple-400">
            <VideoCameraIcon className="w-3 h-3" />
          </span>
          <p className="text-xs text-gray-300 leading-tight">{t('breakout.hintMonitor')}</p>
        </div>
        <div className="flex items-start gap-2">
          <span className="flex-shrink-0 mt-0.5 flex items-center justify-center w-5 h-5 rounded bg-blue-900/60 text-blue-400">
            <SpeakerXMarkIcon className="w-3 h-3" />
          </span>
          <p className="text-xs text-gray-300 leading-tight">{t('breakout.hintSilent')}</p>
        </div>
      </div>

      <button
        onClick={dismiss}
        className="mt-3 w-full text-xs text-gray-400 hover:text-white border border-gray-600 hover:border-gray-500 rounded px-2 py-1 transition-colors"
      >
        {t('breakout.hintDismiss')}
      </button>
    </div>
  );
});

// Memoize the participant item to prevent re-renders
const ParticipantItem = memo(({ participant, onRemove }) => {
  const { t } = useTranslation();
  
  return (
    <div className="text-xs text-gray-300 flex items-center justify-between">
      <div className="flex items-center min-w-0">
        <UserIcon className="w-3 h-3 mr-1.5 text-gray-500 flex-shrink-0" />
        <span className="truncate">{participant.name || participant.identity}</span>
      </div>
      {onRemove && (
        <button
          onClick={onRemove}
          className="p-0.5 text-gray-400 hover:text-red-400 transition-colors flex-shrink-0 ml-2"
          title={t('breakout.returnToMain')}
        >
          <XMarkIcon className="w-3 h-3" />
        </button>
      )}
    </div>
  );
});

ParticipantItem.displayName = 'ParticipantItem';

const BreakoutRoomPanel = memo(({
  virtualRooms = [],
  participantVirtualRooms = {},
  teacherBroadcastRooms = new Set(),
  teacherMonitorRooms = new Set(),
  teacherMonitorSilentRooms = new Set(), // NEW
  isAllBroadcasting = false,
  isAllMonitoring = false,
  isAllMonitoringSilent = false,
  onCreateRoom,
  onDeleteRoom,
  onRenameRoom,
  onAssignParticipant,
  onToggleBroadcast,
  onToggleMonitor,
  onToggleMonitorSilent, // NEW
  onToggleAllBroadcast,
  onToggleAllMonitor,
  onToggleAllMonitorSilent,
  onCreateIndividualRooms
}) => {
  const { t } = useTranslation();
  const [renamingRoom, setRenamingRoom] = useState(null);
  const [newRoomName, setNewRoomName] = useState('');
  const [assigningToRoom, setAssigningToRoom] = useState(null);
  const [roomOperationLoading, setRoomOperationLoading] = useState(false);
  
  const liveParticipants = useParticipants();
  const room = useRoomContext();
  
  // OPTIMIZED: Use state instead of useMemo to avoid re-parsing on every array change
  const [participantMetadataMap, setParticipantMetadataMap] = useState(new Map());
  
  // Listen to metadata changes and update only when metadata actually changes
  useEffect(() => {
    if (!room) return;
    
    const updateMetadata = (prevMetadata, participant) => {
      setParticipantMetadataMap(prev => {
        const newMap = new Map(prev);
        try {
          const metadata = participant.metadata ? JSON.parse(participant.metadata) : {};
          newMap.set(participant.identity, metadata);
        } catch {
          newMap.set(participant.identity, {});
        }
        return newMap;
      });
    };

    const handleParticipantConnected = (participant) => {
      setParticipantMetadataMap(prev => {
        const newMap = new Map(prev);
        try {
          const metadata = participant.metadata ? JSON.parse(participant.metadata) : {};
          newMap.set(participant.identity, metadata);
        } catch {
          newMap.set(participant.identity, {});
        }
        return newMap;
      });
    };

    const handleParticipantDisconnected = (participant) => {
      setParticipantMetadataMap(prev => {
        const newMap = new Map(prev);
        newMap.delete(participant.identity);
        return newMap;
      });
    };

    room.on('participantMetadataChanged', updateMetadata);
    room.on('participantConnected', handleParticipantConnected);
    room.on('participantDisconnected', handleParticipantDisconnected);
    
    // Initial load - parse metadata for existing participants
    liveParticipants.forEach(p => {
      try {
        const metadata = p.metadata ? JSON.parse(p.metadata) : {};
        setParticipantMetadataMap(prev => {
          const newMap = new Map(prev);
          newMap.set(p.identity, metadata);
          return newMap;
        });
      } catch {
        setParticipantMetadataMap(prev => {
          const newMap = new Map(prev);
          newMap.set(p.identity, {});
          return newMap;
        });
      }
    });

    return () => {
      room.off('participantMetadataChanged', updateMetadata);
      room.off('participantConnected', handleParticipantConnected);
      room.off('participantDisconnected', handleParticipantDisconnected);
    };
  }, [room, liveParticipants.length]);
  
  // Memoize unique rooms calculation
  const uniqueRooms = useMemo(() => 
    virtualRooms.filter((room, index, self) =>
      index === self.findIndex((r) => r.id === room.id)
    ), 
    [virtualRooms]
  );
  
  // Memoize unassigned students calculation
  const unassignedStudents = useMemo(() => 
    liveParticipants.filter(p => {
      const metadata = participantMetadataMap.get(p.identity) || {};
      const isTeacherRole = metadata.role === 'teacher';
      const virtualRoomId = metadata.virtualRoomId || participantVirtualRooms[p.identity];
      return !isTeacherRole && !virtualRoomId;
    }),
    [liveParticipants, participantMetadataMap, participantVirtualRooms]
  );

  // Memoize callbacks
  const handleRenameSubmit = useCallback((roomId) => {
    if (newRoomName.trim()) {
      onRenameRoom(roomId, newRoomName);
      setRenamingRoom(null);
      setNewRoomName('');
    }
  }, [newRoomName, onRenameRoom]);

  const handleStartRename = useCallback((room) => {
    setRenamingRoom(room.id);
    setNewRoomName(room.display_name || room.room_name || t('videoGrid.room', { number: room.room_number }));
  }, [t]);

  const handleToggleAssigning = useCallback((roomId) => {
    setAssigningToRoom(current => current === roomId ? null : roomId);
  }, []);

  const handleAssignAndClose = useCallback((participantIdentity, roomId) => {
    onAssignParticipant(participantIdentity, roomId);
    setAssigningToRoom(null);
  }, [onAssignParticipant]);

  // Memoize function to get participants for a room
  const getParticipantsForRoom = useCallback((roomId) => {
    return liveParticipants.filter(p => {
      const metadata = participantMetadataMap.get(p.identity) || {};
      return metadata.virtualRoomId === roomId || participantVirtualRooms[p.identity] === roomId;
    });
  }, [liveParticipants, participantMetadataMap, participantVirtualRooms]);

  return (
    <div className="h-full flex flex-col">
      <div className="p-4 border-b border-gray-700 flex-shrink-0">
        <h3 className="text-lg font-semibold text-white mb-3">{t('breakout.title')}</h3>
        
        <div className="space-y-2">
          <button
            onClick={onCreateRoom}
            disabled={roomOperationLoading}
            className="w-full px-4 py-2.5 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-500 text-white rounded-lg text-sm font-medium flex items-center justify-center transition-colors"
          >
            {roomOperationLoading ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                {t('breakout.processing')}
              </>
            ) : (
              <>
                <PlusCircleIcon className="w-5 h-5 mr-2" />
                {t('breakout.addRoom')}
              </>
            )}
          </button>
          
          <button
            onClick={onCreateIndividualRooms}
            disabled={roomOperationLoading || unassignedStudents.length === 0}
            className="w-full px-4 py-2.5 bg-cyan-600 hover:bg-cyan-700 disabled:bg-gray-500 text-white rounded-lg text-sm font-medium flex items-center justify-center transition-colors"
            title={unassignedStudents.length === 0 ? t('breakout.noStudentsInMain') : t('breakout.createIndividualTooltip')}
          >
            <UserIcon className="w-5 h-5 mr-2" />
            {t('breakout.createIndividual')}
          </button>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {/* One-time onboarding hint — explains the 3 action buttons */}
        {uniqueRooms.length > 0 && <BreakoutRoomHint />}
        {/* All Rooms Control */}
        {uniqueRooms.length > 0 && (
          <div className="bg-gray-700 rounded-lg p-2 border border-gray-600">
            <div className="flex items-center">
              <div className="flex items-center min-w-0 flex-1">
                <QueueListIcon className="w-3.5 h-3.5 mr-1.5 text-cyan-500 flex-shrink-0" />
                <span className="text-xs font-medium text-white whitespace-nowrap">{t('breakout.allRooms')}</span>
                <span className="ml-1.5 text-xs text-gray-400 flex-shrink-0 invisible">(0)</span>
              </div>
              
              <div className="flex items-center gap-0.5 flex-shrink-0 ml-1.5">
                <button
                  onClick={onToggleAllBroadcast}
                  className={`p-1.5 rounded transition-colors ${
                    isAllBroadcasting 
                      ? 'text-cyan-500 bg-cyan-900/50' 
                      : 'text-gray-400 hover:text-cyan-500'
                  }`}
                  title={isAllBroadcasting ? t('breakout.leaveAllRooms') : t('breakout.joinAllRooms')}
                >
                  <PlusIcon className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={onToggleAllMonitor}
                  className={`p-1.5 rounded transition-colors ${
                    isAllMonitoring 
                      ? 'text-purple-500 bg-purple-900/50' 
                      : 'text-gray-400 hover:text-purple-500'
                  }`}
                  title={isAllMonitoring ? t('breakout.stopMonitoringAll') : t('breakout.monitorAllSecretly')}
                >
                  <VideoCameraIcon className="w-3.5 h-3.5" />
                </button>
                <button
                  onClick={onToggleAllMonitorSilent}
                  className={`p-1.5 rounded transition-colors ${
                    isAllMonitoringSilent 
                      ? 'text-blue-500 bg-blue-900/50' 
                      : 'text-gray-400 hover:text-blue-500'
                  }`}
                  title={isAllMonitoringSilent ? t('breakout.stopMonitoringSilentlyAll') : t('breakout.monitorAllRoomsSilently')}
                >
                  <SpeakerXMarkIcon className="w-3.5 h-3.5" />
                </button>
              </div>
              
              <div className="flex items-center gap-0.5 flex-shrink-0 border-l border-gray-600 ml-1.5 pl-1.5 invisible pointer-events-none">
                <div className="p-1.5">
                  <UserPlusIcon className="w-3.5 h-3.5" />
                </div>
                <div className="p-1.5">
                  <PencilIcon className="w-3.5 h-3.5" />
                </div>
                <div className="p-1.5">
                  <TrashIcon className="w-3.5 h-3.5" />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Main Room */}
        <div className="bg-gray-800 rounded-lg p-2 border border-gray-700">
          <div className="flex items-center">
            <div className="flex items-center min-w-0 flex-1">
              <UserGroupIcon className="w-3.5 h-3.5 mr-1.5 text-cyan-500 flex-shrink-0" />
              <span className="text-xs font-medium text-white truncate">{t('liveSession.participants.mainRoom')}</span>
              <span className="ml-1.5 text-xs text-gray-400 flex-shrink-0">
                ({unassignedStudents.length})
              </span>
            </div>
          </div>
          
          {unassignedStudents.length > 0 && (
            <div className="mt-2 pl-5 space-y-1">
              {unassignedStudents.map(participant => (
                <ParticipantItem key={participant.identity} participant={participant} />
              ))}
            </div>
          )}
        </div>

        {/* Breakout Rooms */}
        {uniqueRooms.map((room) => {
          const isBroadcasting = teacherBroadcastRooms.has(room.id);
          const isMonitoring = teacherMonitorRooms.has(room.id);
          const isMonitoringSilent = teacherMonitorSilentRooms.has(room.id); // NEW
          const assignedParticipants = getParticipantsForRoom(room.id);
          
          return (
            <div key={room.id} className="bg-gray-800 rounded-lg p-2 border border-gray-700">
              <div className="flex items-center">
                <div className="flex items-center min-w-0 flex-1">
                  <Squares2X2Icon className="w-3.5 h-3.5 mr-1.5 text-cyan-500 flex-shrink-0" />
                  {renamingRoom === room.id ? (
                    <input
                      type="text"
                      value={newRoomName}
                      onChange={(e) => setNewRoomName(e.target.value)}
                      onBlur={() => handleRenameSubmit(room.id)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          handleRenameSubmit(room.id);
                        }
                      }}
                      className="bg-gray-700 text-white px-1 py-0.5 rounded text-xs flex-1 min-w-0"
                      autoFocus
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <>
                      <span className="text-xs font-medium text-white truncate">
                        {room.display_name || room.room_name || t('videoGrid.room', { number: room.room_number })}
                      </span>
                      <span className="ml-1.5 text-xs text-gray-400 flex-shrink-0">
                        ({assignedParticipants.length})
                      </span>
                    </>
                  )}
                </div>
                
                <div className="flex items-center gap-0.5 flex-shrink-0 ml-1.5">
                  <button
                    onClick={() => onToggleBroadcast(room.id)}
                    className={`p-1.5 rounded transition-colors ${
                      isBroadcasting 
                        ? 'text-cyan-500 bg-cyan-900/50' 
                        : 'text-gray-400 hover:text-cyan-500'
                    }`}
                    title={isBroadcasting ? t('breakout.leaveRoom') : t('breakout.joinRoom')}
                  >
                    <PlusIcon className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => onToggleMonitor(room.id)}
                    className={`p-1.5 rounded transition-colors ${
                      isMonitoring 
                        ? 'text-purple-500 bg-purple-900/50' 
                        : 'text-gray-400 hover:text-purple-500'
                    }`}
                    title={isMonitoring ? t('breakout.stopMonitoring') : t('breakout.monitorSecretly')}
                  >
                    <VideoCameraIcon className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => onToggleMonitorSilent(room.id)}
                    className={`p-1.5 rounded transition-colors ${
                      isMonitoringSilent 
                        ? 'text-blue-500 bg-blue-900/50' 
                        : 'text-gray-400 hover:text-blue-500'
                    }`}
                    title={isMonitoringSilent ? t('breakout.stopMonitoringSilently') : t('breakout.monitorSilently')}
                  >
                    <SpeakerXMarkIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
                
                <div className="flex items-center gap-0.5 flex-shrink-0 border-l border-gray-600 ml-1.5 pl-1.5">
                  {assigningToRoom === room.id ? (
                    <button
                      onClick={() => setAssigningToRoom(null)}
                      className="p-1.5 text-red-400 hover:text-red-500 transition-colors"
                      title={t('common.cancel')}
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  ) : (
                    <button
                      onClick={() => handleToggleAssigning(room.id)}
                      className="p-1.5 text-gray-400 hover:text-cyan-500 transition-colors"
                      title={t('breakout.addParticipant')}
                      disabled={unassignedStudents.length === 0}
                    >
                      <UserPlusIcon className="w-3.5 h-3.5" />
                    </button>
                  )}
                  <button
                    onClick={() => handleStartRename(room)}
                    className="p-1.5 text-gray-400 hover:text-cyan-500 transition-colors"
                    title={t('breakout.renameRoom')}
                  >
                    <PencilIcon className="w-3.5 h-3.5" />
                  </button>
                  <button
                    onClick={() => onDeleteRoom(room.id)}
                    className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                    title={t('breakout.deleteRoom')}
                  >
                    <TrashIcon className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>

              {assigningToRoom === room.id && unassignedStudents.length > 0 && (
                <div className="mt-2 p-2 bg-gray-900 rounded">
                  <p className="text-xs text-gray-400 mb-1">{t('breakout.selectParticipant')}</p>
                  <div className="space-y-1 max-h-24 overflow-y-auto">
                    {unassignedStudents.map(participant => (
                      <button
                        key={participant.identity}
                        onClick={() => handleAssignAndClose(participant.identity, room.id)}
                        className="w-full text-left px-2 py-1 text-xs hover:bg-gray-800 rounded flex items-center text-gray-200 transition-colors"
                      >
                        <UserIcon className="w-3 h-3 mr-1.5 text-gray-400" />
                        <span className="truncate">{participant.name || participant.identity}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {assignedParticipants.length > 0 && (
                <div className="mt-2 space-y-1">
                  {assignedParticipants.map(participant => (
                    <ParticipantItem 
                      key={participant.identity} 
                      participant={participant}
                      onRemove={() => onAssignParticipant(participant.identity, null)}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {uniqueRooms.length === 0 && (
          <div className="text-center py-12">
            <Squares2X2Icon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
            <p className="text-gray-400 mb-2">{t('breakout.noRoomsYet')}</p>
            <p className="text-sm text-gray-500">{t('breakout.clickToStart')}</p>
          </div>
        )}
      </div>
    </div>
  );
});

BreakoutRoomPanel.displayName = 'BreakoutRoomPanel';

export default BreakoutRoomPanel;
